import React, { useState, useEffect } from 'react';
import { contentApi } from '../../services/api';
import Bearbeitungslog from '../logs/Bearbeitungslog';
import MeldungenSection from '../shared/MeldungenSection';
import DeleteReasonModal from '../shared/DeleteReasonModal';

function PostDetail({ postId, onBack }) {
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreReason, setRestoreReason] = useState('');

  // Fetch post details
  const fetchPost = async () => {
    try {
      const response = await contentApi.getPost(postId);
      setPost(response.data);
    } catch (err) {
      setError('Fehler beim Laden des Beitrags');
      console.error('Post fetch error:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchPost();
      setIsLoading(false);
    };
    loadData();
  }, [postId]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isDeleted = () => {
    return post?.deleted_at != null;
  };

  const getStatusBadge = () => {
    if (isDeleted()) {
      return <span className="status-badge suspended">Gelöscht</span>;
    }
    return <span className="status-badge verified">Aktiv</span>;
  };

  // Issue #408: Delete confirmation with documented reason
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = () => {
    setError('');
    setSuccessMessage('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (reason) => {
    setActionLoading(true);
    try {
      await contentApi.deletePost(postId, reason);
      setSuccessMessage('Beitrag gelöscht');
      setShowDeleteModal(false);
      await fetchPost();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      if (err.response?.data?.error === 'already_deleted') {
        setError('Beitrag ist bereits gelöscht');
        setShowDeleteModal(false);
        throw err;
      }
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const openRestoreModal = () => {
    setRestoreReason('');
    setShowRestoreModal(true);
  };

  const closeRestoreModal = () => {
    setShowRestoreModal(false);
    setRestoreReason('');
  };

  const handleRestore = async () => {
    if (!restoreReason.trim()) {
      setError('Bitte geben Sie einen Grund für die Wiederherstellung an');
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await contentApi.restorePost(postId, restoreReason.trim());
      setSuccessMessage('Beitrag wiederhergestellt');
      closeRestoreModal();
      await fetchPost();
    } catch (err) {
      if (err.response?.data?.error === 'not_deleted') {
        setError('Beitrag ist nicht gelöscht');
      } else if (err.response?.data?.error === 'reason_required') {
        setError('Bitte geben Sie einen Grund für die Wiederherstellung an');
      } else {
        setError('Wiederherstellen fehlgeschlagen: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setActionLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  // Parse images from JSON
  const getImages = () => {
    if (!post?.images) return [];
    try {
      if (Array.isArray(post.images)) {
        return post.images;
      }
      if (typeof post.images === 'string') {
        return JSON.parse(post.images);
      }
      return [];
    } catch {
      return [];
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="page-container">
        <div className="error-message">{error || 'Beitrag nicht gefunden'}</div>
        <button className="secondary-button" onClick={onBack}>Zurück</button>
      </div>
    );
  }

  const images = getImages();

  return (
    <div className="page-container">
      {/* Header with Back Button */}
      <div className="page-header">
        <div>
          <button className="secondary-button" onClick={onBack} style={{ marginBottom: '12px' }}>
            ← Zurück zur Liste
          </button>
          <h1>Beitrag Details</h1>
          <p className="page-subtitle">ID: {post.id}</p>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Section 1: Post Information */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Beitragsinformationen</h2>
          {getStatusBadge()}
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Erstellt am</label>
            <span>{formatDate(post.created_at)}</span>
          </div>
          <div className="info-item">
            <label>Aktualisiert am</label>
            <span>{formatDate(post.updated_at)}</span>
          </div>
          <div className="info-item">
            <label>Anonym</label>
            <span>{post.is_anonymous ? <span className="anonym-checkmark">✓</span> : '-'}</span>
          </div>
        </div>

        <div className="info-item full-width" style={{ marginTop: '16px' }}>
          <label>Inhalt</label>
          <div className="content-preview">{post.content}</div>
        </div>

        {/* Images Section */}
        {images.length > 0 && (
          <div className="info-item full-width" style={{ marginTop: '16px' }}>
            <label>Bilder ({images.length})</label>
            <div className="images-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {images.map((imageUrl, index) => (
                <a
                  key={index}
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block' }}
                >
                  <img
                    src={imageUrl}
                    alt={`Bild ${index + 1}`}
                    style={{
                      maxWidth: '150px',
                      maxHeight: '150px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid var(--border-color)'
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </a>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Section 2: Author Information */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Autor</h2>
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Name</label>
            <span>{post.is_anonymous ? <em>Anonym</em> : post.author_name}</span>
          </div>
          <div className="info-item">
            <label>E-Mail</label>
            <span>{post.author_email}</span>
          </div>
          <div className="info-item">
            <label>Hochschule</label>
            <span>{post.author_university || '-'}</span>
          </div>
        </div>
      </section>

      {/* Section 3: Engagement Metrics */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Engagement</h2>
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Likes</label>
            <span className="metric-value">{post.hearts_count || 0}</span>
          </div>
          <div className="info-item">
            <label>Kommentare</label>
            <span className="metric-value">{post.comments_count || 0}</span>
          </div>
          <div className="info-item">
            <label>Meldungen</label>
            <span className={`metric-value ${(post.report_count || 0) >= 5 ? 'warning' : ''}`}>
              {post.report_count || 0}
            </span>
          </div>
        </div>
      </section>

      {/* Section 4: Deletion Info (if deleted) */}
      {isDeleted() && (
        <section className="detail-section warning-section">
          <div className="section-header">
            <h2>Löschung</h2>
          </div>

          <div className="info-grid">
            <div className="info-item">
              <label>Gelöscht am</label>
              <span>{formatDate(post.deleted_at)}</span>
            </div>
            <div className="info-item">
              <label>Gelöscht von</label>
              <span>
                {post.deleted_by_creator === true ? 'Ersteller' :
                 post.deleted_by_creator === false ? 'Administrator' :
                 '-'}
              </span>
            </div>
            <div className="info-item">
              <label>Automatisch gelöscht</label>
              <span>
                {post.deleted_by_creator === false ? (
                  <span className="anonym-checkmark">✓</span>
                ) : '-'}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Section 5: Actions */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Aktionen</h2>
        </div>

        <div className="actions-grid">
          {/* Delete/Restore Actions */}
          <div className="action-card">
            <h3>Beitragsverwaltung</h3>
            <div className="action-buttons-group">
              {isDeleted() ? (
                <button
                  className="action-btn activate"
                  onClick={openRestoreModal}
                  disabled={actionLoading}
                  title="Beitrag wiederherstellen"
                >
                  ↩ Wiederherstellen
                </button>
              ) : (
                <button
                  className="action-btn delete"
                  onClick={handleDelete}
                  disabled={actionLoading}
                  title="Beitrag löschen"
                >
                  🗑️ Löschen
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Issue #410: Meldungen section (shown only when reports exist) */}
      <MeldungenSection targetType="post" targetId={postId} />

      {/* Section 6: Bearbeitungslog (Issue #159) */}
      <Bearbeitungslog targetType="post" targetId={postId} />

      {/* Restore Reason Modal */}
      {showRestoreModal && (
        <div className="modal-overlay" onClick={closeRestoreModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Beitrag wiederherstellen</h2>
            <p className="modal-description">
              Bitte geben Sie einen Grund für die Wiederherstellung an.
              Dieser wird für Audit-Zwecke gespeichert.
            </p>
            <div className="form-group">
              <label htmlFor="restore-reason">Grund für Wiederherstellung *</label>
              <textarea
                id="restore-reason"
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                placeholder="Bitte geben Sie einen Grund an..."
                rows={4}
                className="modal-textarea"
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button
                className="secondary-button"
                onClick={closeRestoreModal}
                disabled={actionLoading}
              >
                Abbrechen
              </button>
              <button
                className="action-btn activate"
                onClick={handleRestore}
                disabled={actionLoading || !restoreReason.trim()}
              >
                {actionLoading ? 'Wird wiederhergestellt...' : 'Wiederherstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue #408: Delete-with-reason modal */}
      <DeleteReasonModal
        open={showDeleteModal}
        title="Beitrag löschen"
        description="Sie sind dabei, diesen Beitrag zu löschen. Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}

export default PostDetail;
